// Resolve any site target AT-URI into a common {site?, deployment?, manifest} context.
//
// Accepts a `app.szzt.site` (follow `current` → deployment → `tile` → manifest), a
// `app.szzt.deployment` (→ `tile` → manifest), or an `ing.dasl.masl` record directly (manifest
// only, no signed deployment context). Fetches raw records via the `RepoReader` seam; performs NO
// integrity checks itself — the caller (verify / gateway) re-derives and compares CIDs. Shape
// violations throw `ResolveError`.

import { parseAtUri } from "./recompute.ts";
import {
  type DeploymentRecordValue,
  type FetchedRecord,
  type MaslRecordValue,
  RecordNotFoundError,
  type RepoReader,
  ResolveError,
  type SiteRecordValue,
  type StrongRef,
} from "./types.ts";

/** The three record collections (NSIDs) that make up a site (§4). */
export const COLLECTION = {
  site: "app.szzt.site",
  deployment: "app.szzt.deployment",
  masl: "ing.dasl.masl",
} as const;

/**
 * `at://<did>/app.szzt.site/<rkey>` — the canonical site-record AT-URI, and the pointer-cache key the
 * gateway's mutable (TTL-bounded) keyspace is built from. The single definition on purpose: the pointer
 * cache's safety argument depends on a `refresh()` key never colliding with a version-pinned
 * (`COLLECTION.deployment`) key, which only holds while every site-key builder agrees byte-for-byte.
 */
export function siteUri(did: string, siteRkey: string): string {
  return `at://${did}/${COLLECTION.site}/${siteRkey}`;
}

/** A fetched record paired with its typed value view. */
export interface Resolved<T> {
  ref: FetchedRecord;
  value: T;
}

/** A fully resolved target: the manifest, plus whatever signed context led to it. */
export interface ResolvedTarget {
  did: string;
  site?: Resolved<SiteRecordValue>;
  deployment?: Resolved<DeploymentRecordValue>;
  manifest: Resolved<MaslRecordValue>;
}

function asObject(value: unknown, what: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new ResolveError(`${what} is not an object`);
  }
  return value as Record<string, unknown>;
}

/** Read a `{ $link }` cid-link field as its CID string. */
export function readLink(value: unknown, field: string, what: string): string {
  const obj = asObject(value, what);
  const link = obj[field];
  if (typeof link === "object" && link !== null && typeof (link as { $link?: unknown }).$link === "string") {
    return (link as { $link: string }).$link;
  }
  throw new ResolveError(`${what}.${field} is not a { $link } cid-link`);
}

/**
 * Read a CID field that upstream declares `type: string, format: cid` — today, only the
 * `ing.dasl.masl` record's own `cid`. The upstream lexicon says plain string, and stock consumers
 * (the appmosphe.re index, `@dasl/tiles` loaders) read it as one, so that is what the write path
 * emits. Records published before 2026-10 instead carry a `{ $link }` cid-link (records are
 * immutable, so those exist forever) — this reader accepts both forms so the verify/serve paths
 * keep proving old and new records alike. NOT a license to write the link form: it is accepted
 * for history, not endorsed for new writes.
 */
export function readCidField(value: unknown, field: string, what: string): string {
  const obj = asObject(value, what);
  const cid = obj[field];
  if (typeof cid === "string") return cid;
  if (typeof cid === "object" && cid !== null && typeof (cid as { $link?: unknown }).$link === "string") {
    return (cid as { $link: string }).$link;
  }
  throw new ResolveError(`${what}.${field} is neither a CID string nor a { $link } cid-link`);
}

/** Read a `com.atproto.repo.strongRef` (uri + plain cid string) field. */
export function readStrongRef(value: unknown, field: string, what: string): StrongRef {
  const obj = asObject(value, what);
  const ref = asObject(obj[field], `${what}.${field}`);
  if (typeof ref.uri !== "string" || typeof ref.cid !== "string") {
    throw new ResolveError(`${what}.${field} is not a strong ref (uri + cid)`);
  }
  return { uri: ref.uri, cid: ref.cid };
}

async function fetchRecord(reader: RepoReader, uri: string): Promise<FetchedRecord> {
  const rec = await reader.getRecord(uri);
  if (rec === null) throw new RecordNotFoundError(`record not found: ${uri}`);
  return rec;
}

/**
 * Resolve `target` (a site, deployment, or manifest AT-URI) to its manifest and signed context.
 * Follows strong refs by their AT-URI; the referenced CIDs are checked later by the caller.
 */
export async function resolveTarget(reader: RepoReader, target: string): Promise<ResolvedTarget> {
  const { did, collection } = parseAtUri(target);

  let site: Resolved<SiteRecordValue> | undefined;
  let deploymentUri: string | undefined;

  if (collection === COLLECTION.site) {
    const ref = await fetchRecord(reader, target);
    const current = readStrongRef(ref.value, "current", "app.szzt.site");
    site = { ref, value: ref.value as SiteRecordValue };
    deploymentUri = current.uri;
  } else if (collection === COLLECTION.deployment) {
    deploymentUri = target;
  } else if (collection === COLLECTION.masl) {
    const ref = await fetchRecord(reader, target);
    return { did, manifest: { ref, value: ref.value as MaslRecordValue } };
  } else {
    throw new ResolveError(`unsupported target collection: ${collection}`);
  }

  const depRef = await fetchRecord(reader, deploymentUri);
  const tile = readStrongRef(depRef.value, "tile", "app.szzt.deployment");
  const maslRef = await fetchRecord(reader, tile.uri);

  return {
    did,
    ...(site !== undefined ? { site } : {}),
    deployment: { ref: depRef, value: depRef.value as DeploymentRecordValue },
    manifest: { ref: maslRef, value: maslRef.value as MaslRecordValue },
  };
}
